import { Autocomplete } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useCallback, useEffect, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioAttoVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

export const ID_OTHER_AUTHORITY = '0';
export const ID_OBITORIO_AUTHORITY = '14';
export const ID_PROCURA_AUTHORITY = '15';
export const ID_MEDICINA_LEGALE = '105';

export interface ItemValoreDominioAttoVM extends ItemVM {
  idDominioPadre?: string;
  idValorePadre?: string;
}

interface AutocompleteAuthorityProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (value?: ItemValoreDominioAttoVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio?: string;
  dataReference?: DateTime | null;
  label?: string;
  onlyId?: Array<number>; // array dei soli ID da mostrare
}

const AutocompleteAuthorityComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  idDominio = '6',
  dataReference,
  label = 'Autorità',
  onlyId,
}: AutocompleteAuthorityProps): React.ReactElement => {
  const [authorities, getAuthorities] = useHttpFetch<
    HttpResponse<Array<ValoreDominioAttoVO>>,
    Array<ItemValoreDominioAttoVM>
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) => {
      let castedResponse = (response.data.payload || []) as Array<ValoreDominioAttoVO>;

      if (onlyId && onlyId.length) {
        castedResponse = castedResponse.filter((item) => {
          let res = false;

          onlyId.forEach((itemOnlyId) => {
            if (Number.parseInt(item.key?.idValore || '', 10) === itemOnlyId) {
              res = true;
            }
          });

          return res;
        });
      }
      return castedResponse.map((item) => ({
        id: item.key?.idValore?.toString(),
        value: item.descrizioneBreveValore,
        idDominioPadre: item.tipoDominioPadre || '',
        idValorePadre: item.idValorePadre || '',
      })) as Array<ItemValoreDominioAttoVM>;
    },
  });

  const authoritiesData: Array<ItemValoreDominioAttoVM> = useMemo(() => {
    return (authorities?.data || []) as Array<ItemValoreDominioAttoVM>;
  }, [authorities?.data]);

  const defaultProps = useMemo(() => {
    return {
      options: (authorities.data as Array<ItemVM>) || [],
      getOptionLabel: (option: ItemVM) => option.value,
    };
  }, [authorities.data]);

  const selectedValue = defaultProps.options.find((elem) => elem.id === value);

  useEffect(() => {
    if (!idDominio) {
      return;
    }

    const transformedFilters: OperationObject = {
      idDominio: {
        value: idDominio || '',
        operation: Operation.EQUALS,
      },
      dataRiferimento: {
        value: dataReference ? dataReference.toFormat('yyyy-MM-dd') : '',
        operation: Operation.EQUALS,
      },
    };

    const queryString = `${paginationListToQueryString({
      filters: transformedFilters,
      orders: {},
    })}`;

    getAuthorities({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, [idDominio, dataReference]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (authorities.abort) {
          authorities.abort();
        }

        const transformedFilters: OperationObject = {
          idDominio: {
            value: idDominio || '',
            operation: Operation.EQUALS,
          },
          dataRiferimento: {
            value: dataReference ? dataReference.toFormat('yyyy-MM-dd') : '',
            operation: Operation.EQUALS,
          },
          descrizioneBreveValore: { value: searchValue || '', operation: Operation.LIKE },
        };

        const queryString = `${paginationListToQueryString({
          filters: transformedFilters,
          orders: {},
        })}`;

        getAuthorities({
          params: {},
          paramsSerializer: () => queryString,
        });
      },
      [getAuthorities, authorities.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={`${label}-id`}
      value={{ id: selectedValue?.id || '', value: selectedValue?.value || '' }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(authoritiesData.find((item) => item.id === element?.id));
      }}
      disabled={disabled}
      onInputChange={(event, changeValue, reason) => {
        if (Boolean(changeValue) && changeValue.length >= 3 && reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      clearOnBlur={false}
      loading={authorities.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

AutocompleteAuthorityComponent.displayName = 'Autocomplete Authority component';

const AutocompleteAuthority = memo(AutocompleteAuthorityComponent);

export default AutocompleteAuthority as typeof AutocompleteAuthorityComponent;
